/**
 * Transport diagnostics can contain provider bodies, device identifiers, or credentials. Match
 * known failures, but only return product-owned copy; never render an arbitrary error verbatim.
 */
const KEPT = 'Your files remain available in this conversation.';
const GENERIC_FILE_COPY = `Gyro could not read an attached file. Use an unlocked, valid file; for archives, try a smaller ZIP with individual documents. ${KEPT}`;
/**
 * Why gyro-svc (v73+) could not read a file: the closed-set `reason` it sends next to `file_cannot_be_read`
 * (FILE_REFUSAL_REASONS in services/gyro-svc/src/fileExtraction.ts). One fixed sentence each; never the server's text.
 */
const FILE_REFUSAL_COPY = {
    // The service sends file_encrypted for a locked Office document (or an old binary Office file) AND for an encrypted ZIP.
    file_encrypted: `Gyro could not read an attached file because it is password-protected (a locked Office document or ZIP archive) or saved in an old binary Office format. Attach an unprotected copy: an .xlsx, .docx or .pptx file, or a ZIP made without a password. ${KEPT}`,
    file_damaged: `Gyro could not read an attached file because it is damaged or incomplete. Open it in its app, save a fresh copy and attach that. ${KEPT}`,
    file_wrong_type: `Gyro could not read an attached file because its contents do not match its file type (for example, a renamed file). Save it in its real format and attach it again. ${KEPT}`,
    file_unsupported_format: `Gyro could not read an attached file because it uses a format feature Gyro does not support. Save it as a standard .xlsx, .docx, .pptx or ZIP file and attach it again. ${KEPT}`,
    file_unsafe_paths: `Gyro could not read an attached archive because it contains unsafe or duplicate file paths. Make a new ZIP of just the files you need. ${KEPT}`,
    file_too_large: `Gyro could not read an attached file because it expands beyond Gyro's reading limit when opened (64 MB for an Office file, 16 MB for an archive). Split it into smaller files. ${KEPT}`,
    file_too_complex: `Gyro could not read an attached file because it is too complex to read (too many parts, or nested too deeply). Simplify it or split it into smaller files. ${KEPT}`,
    file_not_text: `Gyro could not read an attached file as text. Save it as UTF-8 text and attach it again. ${KEPT}`,
    file_empty: `Gyro could not read an attached file because it has nothing to read (no worksheets, slides or files). ${KEPT}`,
    // Gyro's own reader failed, not the file: never tell the user their file is damaged.
    file_internal_error: `Gyro could not read an attached file because of a problem in Gyro, not in your file. Send your message with the files again; if it keeps happening, tell IT. ${KEPT}`,
};
/** The other attachment refusal codes gyro-svc returns before or instead of reading a file. */
const ATTACHMENT_REFUSAL_COPY = {
    file_cannot_be_read: GENERIC_FILE_COPY,
    invalid_attachment_encoding: GENERIC_FILE_COPY,
    attachment_not_base64: GENERIC_FILE_COPY,
    invalid_attachment_data: GENERIC_FILE_COPY,
    invalid_attachment: GENERIC_FILE_COPY,
    unsupported_attachment_kind: GENERIC_FILE_COPY,
    attachments_must_be_array: GENERIC_FILE_COPY,
    attachment_text_too_large: 'These files contain too much text for one message. Remove some attachments and send a smaller batch.',
    attachment_too_large: `An attached file is larger than 5 MB, Gyro's limit for one file. Attach a smaller file. ${KEPT}`,
    attachments_too_large: `The attached files are larger than 15 MB together, Gyro's limit for one message. Send fewer files at a time. ${KEPT}`,
    too_many_attachments_max_8: `Gyro accepts at most 8 files in one message. Remove some attachments and send again. ${KEPT}`,
    unsupported_media_type: `Gyro cannot read one of the attached file types. Attach PDF, image, Office, text, data or ZIP files. ${KEPT}`,
    invalid_pdf_attachment: `An attached PDF is not a valid PDF file. Save it again as a PDF and attach it. ${KEPT}`,
    file_extraction_disabled: `This Gyro service cannot read Office, text or archive files yet. Images and PDFs still work. ${KEPT}`,
    file_extraction_busy: `Gyro's file reader is busy with other files right now. Send your message with the files again in a moment. ${KEPT}`,
};
/** A closed-set file refusal reason this widget has a sentence for. */
export function isFileRefusalReason(value) {
    return typeof value === 'string' && Object.hasOwn(FILE_REFUSAL_COPY, value);
}
/** An attachment refusal code this widget has a sentence for (the client keeps the code instead of the server's text). */
export function isAttachmentRefusalCode(value) {
    return typeof value === 'string' && Object.hasOwn(ATTACHMENT_REFUSAL_COPY, value);
}
/**
 * The Windows proxy relays fixed refusal copy with a bounded code, rather than a renderer HTTP 400; a browser gets
 * `stream 400: <code>`. Either way the code (a specific reason, or the error code) maps to fixed copy.
 */
export function fileReadFailureMessage(failure) {
    const raw = failure instanceof Error ? failure.message : typeof failure === 'string' ? failure : '';
    const diagnostic = raw.slice(0, 4096).trim();
    const native = /^The assistant refused this request \((?:400|503) ([a-z0-9_]+)\)\. If it keeps happening, tell IT the code\.$/.exec(diagnostic);
    const detail = native?.[1] ?? diagnostic.replace(/^(?:stream|ask) (?:400|503):\s*/, '');
    if (Object.hasOwn(FILE_REFUSAL_COPY, detail))
        return FILE_REFUSAL_COPY[detail];
    if (Object.hasOwn(ATTACHMENT_REFUSAL_COPY, detail))
        return ATTACHMENT_REFUSAL_COPY[detail];
    return null;
}
/**
 * gyro-svc's own sentence for a 429 (the ask routes' rate limit names the limit and when to try again), shown the way
 * historySave.ts shows a save's: one plain line of bounded length, with no markup, no control characters and no
 * braces, and never a bare code. client.ts keeps a 429's detail to the JSON body's message or text, so a proxy's page
 * never reaches here as a sentence.
 */
function rateLimitSentence(status, detail) {
    const text = detail.trim();
    return status === 429 && text && text.length <= 400 && !/[\u0000-\u001f\u007f<>{}]/.test(text) && !/^[a-z0-9_]+$/.test(text) ? text : undefined;
}
/** The desktop agent relays a refusal of a native ask as this fixed sentence with its status and code, never the body. */
const NATIVE_REFUSAL = /^The assistant refused this request \((\d{3}) ([a-z0-9_]+)\)\. If it keeps happening, tell IT the code\.$/;
const RATE_LIMITED_COPY = 'Gyro is receiving too many requests. Wait a moment, then try again.';
const USAGE_LIMIT_COPY = 'Your Gyro usage limit has been reached. Wait for it to reset or ask your administrator to adjust it.';
/**
 * The 429 codes the desktop relays and the limit each one names (Usage caps). The server's own turn_limit sentence gives
 * the number; the desktop relays only the code, so this one names the limit without it. budget_exhausted is not an ask
 * route's 429 today (a spend cap answers 200 with the server's own sentence; only history compaction sends 429
 * budget_exhausted), so it is here only so a relay of it would still name the limit.
 */
const NATIVE_LIMIT_COPY = {
    rate_limited: RATE_LIMITED_COPY,
    turn_limit: 'You already have as many answers in progress as one person can have running at once. Wait for one of them to finish, then send this question again.',
    budget_exhausted: USAGE_LIMIT_COPY,
};
/**
 * The fixed sentence for a 429 the desktop relayed as its native envelope ("The assistant refused this request (429
 * <code>)…"): the limit that refused it, or the rate-limit sentence for any other 429 code. Undefined for anything else.
 */
export function nativeLimitMessage(failure) {
    const raw = failure instanceof Error ? failure.message : typeof failure === 'string' ? failure : '';
    const native = NATIVE_REFUSAL.exec(raw.slice(0, 4096).trim());
    const code = native?.[2];
    if (native?.[1] !== '429' || !code)
        return undefined;
    return Object.hasOwn(NATIVE_LIMIT_COPY, code) ? NATIVE_LIMIT_COPY[code] : RATE_LIMITED_COPY;
}
/**
 * The text of an ask that ended in mode 'error' (an answer, not a failed request), as the chat shows it: a file
 * refusal's or a relayed limit refusal's fixed sentence (the desktop answers a refused buffered ask with a 200 that
 * carries its native envelope), else the server's own sentence unchanged (a spend cap, a policy refusal).
 */
export function errorReplyText(text) {
    return fileReadFailureMessage(text) ?? nativeLimitMessage(text) ?? text;
}
export function askFailureMessage(failure) {
    const fileIssue = fileReadFailureMessage(failure);
    if (fileIssue)
        return fileIssue;
    const raw = failure instanceof Error ? failure.message : typeof failure === 'string' ? failure : '';
    const diagnostic = raw.slice(0, 4096).trim();
    const http = /^(?:stream|ask) (\d{3})(?::\s*(.*))?$/s.exec(diagnostic);
    const status = http ? Number(http[1]) : undefined;
    const detail = http?.[2] ?? diagnostic;
    // Only the detail of an HTTP diagnostic is the server's: a bare "stream 429" has none.
    const limit = rateLimitSentence(status, http?.[2] ?? '');
    if (limit)
        return limit;
    // The desktop relays the ask routes' 429s by status and code only (the server's sentence stays in the agent): name
    // the limit that refused it, not "could not complete this response".
    const limit429 = nativeLimitMessage(diagnostic);
    if (limit429)
        return limit429;
    if (detail === 'app_auth_required' || detail === 'This PC is not configured for Gyro yet (application key). Contact IT.') {
        return 'This PC is not configured for Gyro yet. Ask IT to check its application key, then try again.';
    }
    if (['bad_signature', 'device_key_mismatch'].includes(detail)
        || detail === "This Gyro window is using a device identity the service does not recognize. Close it and open Gyro from the Start menu, or ask IT to check this PC's enrollment.") {
        return "This Gyro window is using a device identity the service does not recognize. Close it and open Gyro from the Start menu, or ask IT to check this PC's enrollment.";
    }
    if (detail === 'Gyro could not verify this device or your sign-in. Reopen Gyro from the Start menu and try again. If it persists, contact IT.') {
        return 'Gyro could not verify this device or your sign-in. Reopen Gyro from the Start menu and try again. If it persists, contact IT.';
    }
    if (['device_not_enrolled', 'device_enrollment_not_authorized', 'device_key_conflict'].includes(detail)
        || detail.startsWith('This PC is not enrolled with Gyro yet (device ')) {
        return 'This PC is not enrolled with Gyro yet. Ask IT to authorize this device, then try again.';
    }
    if (detail === 'entra_identity_unavailable'
        || detail === 'Microsoft 365 sign-in is temporarily unavailable, so Gyro cannot confirm who you are. Try again in a minute.') {
        return 'Microsoft 365 sign-in is temporarily unavailable. Wait a minute, then try again.';
    }
    if (['unauthenticated', 'entra_bearer_required', 'entra_bearer_invalid', 'entra_identity_mismatch'].includes(detail)
        || detail === 'Gyro needs your Microsoft 365 sign-in. Sign in when prompted, or sign out of Windows and back in, then try again.') {
        return 'Gyro needs you to sign in again. Reconnect your account, then try again.';
    }
    if (detail === 'budget_exhausted') {
        return USAGE_LIMIT_COPY;
    }
    if (detail === 'rate_limited' || status === 429) {
        return RATE_LIMITED_COPY;
    }
    if (detail === 'Gyro took too long to answer (over two minutes) and gave up. Try again, or ask a narrower question.'
        || status === 408 || status === 504) {
        return 'Gyro took too long to answer. Check whether any requested action completed before trying again.';
    }
    if (detail === 'The assistant service is temporarily unavailable. Try again in a moment.'
        || detail === 'device_registry_unavailable' || status === 502 || status === 503) {
        return 'The assistant service is temporarily unavailable. Wait a moment, then try again.';
    }
    if (status === 401 || status === 403) {
        return 'Gyro could not verify your access. Reconnect your account or ask IT to check your access.';
    }
    if (detail === 'Gyro cannot reach the assistant service right now (network). Check your connection and try again.'
        || ['Failed to fetch', 'fetch failed', 'NetworkError when attempting to fetch resource.', 'Load failed'].includes(detail)) {
        return 'Gyro cannot reach the assistant service. Check your connection, then try again.';
    }
    return 'Gyro could not complete this response. Check whether any requested action completed before trying again.';
}
/** Keep any received text while making it clear that the response did not finish. */
export function failedAskReply(failure, partialText = '') {
    const message = askFailureMessage(failure);
    return { content: partialText ? `${partialText}\n\n${message}` : message, error: true, documentReady: false };
}
//# sourceMappingURL=askFailure.js.map